import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Section, Grid, Button } from '../components/ui';
import SEO from '../components/SEO';

const HeroSection = styled(Section)`
  min-height: 80vh; display: flex; align-items: center;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroContent = styled.div`max-width: 720px;`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 500px;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
  justify-self: end;
  @media (max-width: 968px) {
    justify-self: center;
    max-width: 350px;
  }
`;

const Eyebrow = styled.span`
  display: inline-block; background: #eab308; color: #0f0f0f;
  font-size: 0.875rem; font-weight: 600; padding: 0.25rem 0.75rem;
  border-radius: 9999px; margin-bottom: 1rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem; margin-bottom: 1.5rem;
  @media (max-width: 768px) { font-size: 2.25rem; }
  span { color: #eab308; }
`;

const HeroDesc = styled.p`
  font-size: 1.25rem; color: #6b7280; margin-bottom: 2rem; line-height: 1.625;
  @media (max-width: 768px) { font-size: 1.125rem; }
`;

const HeroBtns = styled.div`display: flex; gap: 1rem; flex-wrap: wrap;`;

const FeaturedText = styled.p`
  text-align: center; color: #6b7280; font-size: 0.875rem;
  text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 1.5rem;
`;

const Logos = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 4rem; flex-wrap: wrap;
`;

const LogoLink = styled(Link)`
  display: block;
`;

const LogoImg = styled.img`
  height: 50px;
  width: auto;
  object-fit: contain;
  transition: all 200ms ease;
  &:hover { filter: grayscale(100%); opacity: 0.7; }
`;

const SectionHeader = styled.div`
  text-align: center; max-width: 640px; margin: 0 auto 3rem;
`;

const SectionTitle = styled.h2`margin-bottom: 1rem;`;

const SectionDesc = styled.p`color: #6b7280; font-size: 1.125rem; margin: 0;`;

const FeatureCard = styled.div`text-align: center; padding: 1.5rem;`;

const FeatureIcon = styled.div`
  width: 64px; height: 64px; background: #fef3c7; border-radius: 0.75rem;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1rem; font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`margin-bottom: 0.5rem; font-size: 1.25rem;`;

const FeatureDesc = styled.p`color: #6b7280; margin: 0;`;

const ProductCard = styled.div`
  background: white; border-radius: 1rem; overflow: hidden;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  transition: transform 250ms ease;
  &:hover { transform: translateY(-4px); }
`;

const ProductImg = styled.div`
  height: 280px; background-image: url(${({ $src }) => $src});
  background-size: cover; background-position: center;
`;

const ProductContent = styled.div`padding: 1.5rem;`;

const ProductPrice = styled.span`
  font-size: 1.5rem; font-weight: 700; color: #eab308;
`;

const ProductName = styled.h3`margin-bottom: 0.5rem;`;

const ProductDesc = styled.p`
  color: #6b7280; font-size: 0.875rem;
  margin: 0.75rem 0 1rem;
`;

const TestimonialCard = styled.div`
  background: white; padding: 2rem; border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
`;

const Quote = styled.blockquote`
  font-size: 1.125rem; font-style: italic; line-height: 1.625;
  margin-bottom: 1rem;
  &::before { content: '"'; color: #eab308; font-size: 2rem; line-height: 0; vertical-align: -0.3em; margin-right: 0.25rem; }
`;

const Author = styled.cite`font-style: normal; color: #6b7280; font-size: 0.875rem;`;

const SpaceSection = styled(Section)`
  background: url("https://images-assets.nasa.gov/image/PIA03654/PIA03654~medium.jpg");
  background-size: cover;
  background-position: center;
  height: 250px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  @media (max-width: 768px) { height: 220px; }
`;

const RotatingImage = styled.img`
  width: 400px;
  height: 400px;
  animation: spin 120s linear infinite;
  margin-top: -10px;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @media (max-width: 768px) { width: 280px; height: 280px; }
`;

const CTATitle = styled.h2`text-align: center; margin-bottom: 1rem;`;

const CTADesc = styled.p`
  text-align: center; color: #6b7280; font-size: 1.125rem;
  max-width: 600px; margin: 0 auto 2rem;
`;

const EmailForm = styled.form`
  display: flex; gap: 0.75rem; max-width: 480px; margin: 0 auto;
  @media (max-width: 480px) { flex-direction: column; }
`;

const EmailInput = styled.input`
  flex: 1; padding: 0.75rem 1rem; font-size: 1rem;
  border: 2px solid #e5e7eb; border-radius: 0.5rem; outline: none;
  &:focus { border-color: #eab308; }
`;

const Home = () => {
  const handleSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    window.location.href = `mailto:tom@thenewcalendar.com?subject=Newsletter Signup&body=Please add me: ${email}`;
  };

  return (
    <>
      <SEO title="The New Calendar" description="The first calendar in history built with math, science, and logic." url="https://thenewcalendar.com" />

      <HeroSection>
        <Container>
          <HeroGrid>
            <HeroContent>
              <Eyebrow>Discover the 5th Season</Eyebrow>
              <HeroTitle>The best calendar in the <span>history of time</span></HeroTitle>
              <HeroDesc>The New Calendar is the first timekeeping system built with math, science, and logic. Take control of your most precious resource.</HeroDesc>
              <HeroBtns>
                <Button as={Link} to="/shop" size="lg">Shop Now</Button>
                <Button as={Link} to="/about" variant="outline" size="lg">Learn More</Button>
              </HeroBtns>
            </HeroContent>
            <HeroImage src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700681548/_199f96fa-e3d5-436b-8cf2-06ec67863871_x7guip.jpg" alt="The New Calendar" />
          </HeroGrid>
        </Container>
      </HeroSection>

      <Section $muted>
        <Container>
          <FeaturedText>Featured on</FeaturedText>
          <Logos>
            <LogoLink to="/media"><LogoImg src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700598946/New%20Calendar/bpbdyc2wnebxupwlkyss.jpg" alt="NPR" /></LogoLink>
            <LogoLink to="/media"><LogoImg src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700598765/New%20Calendar/vr8k31urxb0npsh5022b.png" alt="ABC News" /></LogoLink>
            <LogoLink to="/media"><LogoImg src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700598753/New%20Calendar/evntcoe4y9cuvrxzxqxt.png" alt="iHeartRadio" /></LogoLink>
            <LogoLink to="/media"><LogoImg src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700599773/New%20Calendar/l7erdymgqadij3kvaube.png" alt="Cape Gazette" /></LogoLink>
          </Logos>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader>
            <SectionTitle>Why The New Calendar?</SectionTitle>
            <SectionDesc>For the first time in history, a calendar system designed around how time actually works.</SectionDesc>
          </SectionHeader>
          <Grid cols={3} gap="2rem">
            <FeatureCard>
              <FeatureIcon>🔢</FeatureIcon>
              <FeatureTitle>Mathematically Perfect</FeatureTitle>
              <FeatureDesc>365 = 5 × 73. Five seasons, each exactly 73 days.</FeatureDesc>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>♻️</FeatureIcon>
              <FeatureTitle>Perpetual Design</FeatureTitle>
              <FeatureDesc>Buy once, use forever. Works from 2019 to 9999.</FeatureDesc>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🌿</FeatureIcon>
              <FeatureTitle>Aligned with Nature</FeatureTitle>
              <FeatureDesc>Anchored to the solstices. Track natural patterns.</FeatureDesc>
            </FeatureCard>
          </Grid>
        </Container>
      </Section>

      <Section $muted>
        <Container>
          <SectionHeader>
            <SectionTitle>Shop The Collection</SectionTitle>
            <SectionDesc>Premium calendars designed and manufactured in the USA.</SectionDesc>
          </SectionHeader>
          <Grid cols={2} gap="2rem">
            <ProductCard>
              <ProductImg $src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1700665978/EKJClknXYAgIzI2_pk6dy9.jpg" />
              <ProductContent>
                <ProductPrice>$25</ProductPrice>
                <ProductName>The New Calendar Classic</ProductName>
                <ProductDesc>The original perpetual wall calendar with user guide.</ProductDesc>
                <Button as={Link} to="/newcalendar_classic" $fullWidth>View Product</Button>
              </ProductContent>
            </ProductCard>
            <ProductCard>
              <ProductImg $src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1734522897/IMG_7101copycopy_z9ck6d.jpg" />
              <ProductContent>
                <ProductPrice>$150</ProductPrice>
                <ProductName>Wall Map (3' × 4')</ProductName>
                <ProductDesc>Large format dry-erase planning calendar.</ProductDesc>
                <Button as={Link} to="/newcalendar_wallmap" $fullWidth>View Product</Button>
              </ProductContent>
            </ProductCard>
          </Grid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader><SectionTitle>What People Say</SectionTitle></SectionHeader>
          <Grid cols={3} gap="1.5rem">
            <TestimonialCard>
              <Quote>This actually makes so much sense. Why didn't anyone think of this before?</Quote>
              <Author>— TikTok viewer</Author>
            </TestimonialCard>
            <TestimonialCard>
              <Quote>Changed how I plan my garden and seasonal activities completely.</Quote>
              <Author>— Verified Customer</Author>
            </TestimonialCard>
            <TestimonialCard>
              <Quote>Finally, a calendar that divides the year evenly. Brilliant.</Quote>
              <Author>— Calendar Enthusiast</Author>
            </TestimonialCard>
          </Grid>
        </Container>
      </Section>

      <SpaceSection>
        <RotatingImage 
          src="https://res.cloudinary.com/dqy3fktlv/image/upload/v1701098907/seasongeometry_izizge.png" 
          alt="The New Calendar"
        />
      </SpaceSection>

      <Section $yellow>
        <Container>
          <CTATitle>Time to join The Revolution</CTATitle>
          <CTADesc>Get updates on new products, early access, and exclusive content.</CTADesc>
          <EmailForm onSubmit={handleSubmit}>
            <EmailInput type="email" name="email" placeholder="Enter your email" required />
            <Button type="submit">Subscribe</Button>
          </EmailForm>
        </Container>
      </Section>
    </>
  );
};

export default Home;
